import * as React from 'react';
import { Button, Col, Row } from 'antd';
import { observer } from 'mobx-react';
import { Prompt } from 'react-router';

import { CardController } from 'src/app/shared/card.controller';
import { HeaderView } from 'src/app/shared/header.view';
import { DataLocalService } from 'src/core/services/data-local.service';
import { BooleanView } from 'src/core/ui/boolean.view';
import { Column, DataTableView } from 'src/core/ui/data-table.view';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { TextView } from 'src/core/ui/text.view';
import { TaxonomyEditorController } from 'src/app/general/taxonomies/parts/taxonomy-editor.controller';
import { ZuluIcon } from 'src/app/shared/icon';

interface Props extends ComponentViewProps<DataSet<EntitySchemaProperty>> {
	mode: ControlMode;
	onModeChange: (mode: ControlMode) => void;
	isValidPropertyItem: (item: EntitySchemaProperty) => React.ReactNode[];
	onEditPropertyItem: (item: EntitySchemaProperty) => void;
	createPropertyItem: () => Promise<EntitySchemaProperty>;
	afterDeletePropertyItem: () => void;
	swapDown: (index: number) => void;
	onSave: () => void;
	onCancel: () => void;
	schemaName: string;
}

@observer
export class EntitySchemaFormView extends React.Component<Props, {}> {
	constructor(props: Readonly<Props>) {
		super(props);
	}

	private getOptionsRequest(options: string[]): DropdownRequest<any> {
		const dropdownOptions = options.map(m => ({ value: m, label: m }));
		const dataSet = new DataLocalService<any>(dropdownOptions);
		return {
			query: () => Promise.resolve<QueryResult<any>>({ items: dataSet.items, totalCount: dataSet.totalPages }),
			dataSet: dataSet,
		};
	}

	optionsType = [
		{ value: 'boolean', label: 'Switch' },
		{ value: 'date', label: 'Date' },
		{ value: 'email', label: 'Email' },
		{ value: 'options', label: 'Options' },
		{ value: 'text', label: 'Text' },
		{ value: 'url', label: 'Url' },
		{ value: 'taxonomy', label: 'Taxonomy' }
	];

	columns: Column[] = [
		{
			title: '',
			key: 'index',
			width: '5%',
			render: (text, record, index) => {
				return (
					<div>
						{
							index > 0 &&
							<div>
								<Button
									size="small"
									onClick={() => this.props.swapDown(index - 1)}>
									<ZuluIcon type="caret-up" />
								</Button>
							</div>
						}
						{
							index < (this.props.model.count - 1) &&
							<div>
								<Button
									size="small"
									onClick={() => this.props.swapDown(index)}>
									<ZuluIcon type="caret-down" />
								</Button>
							</div>
						}
					</div>
				);
			}
		},
		{
			title: 'Name',
			key: 'name',
			dataIndex: 'name',
			width: '15%',
			editable: (text: string, record: EntitySchemaProperty, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<TextView
							required
							value={record.name}
							mode={this.props.mode}
							onChanged={(newValue) => {
								record.name = newValue;
								this.props.onEditPropertyItem(record);
							}}
						/>
					);
				}

				return text || "";
			}
		},
		{
			title: 'Label',
			key: 'label',
			dataIndex: 'label',
			width: '20%',
			editable: (text: string, record: EntitySchemaProperty, index: number, isSelected: boolean) => {
				if (isSelected) {
					return (
						<TextView
							required
							value={record.label}
							mode={this.props.mode}
							onChanged={(newValue) => {
								record.label = newValue;
								this.props.onEditPropertyItem(record);
							}}
						/>
					);
				}

				return text || "";
			}
		},
		{
			title: 'Type',
			key: 'type',
			dataIndex: 'type',
			width: '10%',
			editable: (text: EntitySchemaPropertyType, record: EntitySchemaProperty, index: number, isSelected: boolean, refresh: () => void) => (
				<DropdownView
					required
					model={record.type}
					options={this.optionsType}
					label={null}
					disabled={!isSelected || this.props.mode === 'display'}
					onChanged={(newValue) => {
						record.type = newValue.value;
						refresh();
						this.props.onEditPropertyItem(record);
					}}
				/>
			)
		},
		{
			title: 'Required',
			key: 'required',
			dataIndex: 'required',
			width: '10%',
			editable: (value: boolean, record: EntitySchemaProperty, index: number, isSelected: boolean) => (
				<BooleanView
					required
					value={record.required}
					mode={isSelected && record.type !== 'taxonomy' ? this.props.mode : 'display'}
					onChanged={(newValue) => {
						record.required = newValue;
						this.props.onEditPropertyItem(record);
					}}
				/>
			)
		},
		{
			title: 'Config',
			key: 'config',
			dataIndex: 'config',
			width: '40%',
			editable: (value: any, record: EntitySchemaProperty, index: number, isSelected: boolean, refresh: () => void) => {
				return (
					<div>
						{record.type === 'text' &&
							<Row gutter={24}>
								<Col xs={8}>
									<TextView
										label="Min"
										value={record.config.min}
										mode={isSelected ? this.props.mode : 'display'}
										onChanged={(newValue) => {
											record.config.min = newValue;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
								<Col xs={8}>
									<TextView
										label="Max"
										value={record.config.max}
										mode={isSelected ? this.props.mode : 'display'}
										onChanged={(newValue) => {
											record.config.max = newValue;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
								<Col xs={8}>
									<TextView
										label="Pattern"
										value={record.config.pattern}
										mode={isSelected ? this.props.mode : 'display'}
										onChanged={(newValue) => {
											record.config.pattern = newValue;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
							</Row>
						}
						{record.type === 'taxonomy' &&
							<Row gutter={24}>
								<Col xs={8}>
									<TaxonomyEditorController
										required
										label="Taxonomy"
										value={record.taxonomy}
										mode={isSelected ? this.props.mode : 'display'}
										setBusy={this.props.setBusy}
										onChange={(newValue) => {
											record.taxonomy = newValue.jsonSchemaDefinitionName;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
								<Col xs={8}>
									<BooleanView
										label="Multi"
										value={record.config.multi}
										mode={isSelected ? this.props.mode : 'display'}
										onChanged={(newValue) => {
											record.config.multi = newValue;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
								<Col xs={8}>
									<BooleanView
										label="Allow New Tags"
										value={record.config.allowNewTags}
										mode={isSelected ? this.props.mode : 'display'}
										onChanged={(newValue) => {
											if (newValue) {
												record.config.multi = true;
												refresh();
											}
											record.config.allowNewTags = newValue;
											this.props.onEditPropertyItem(record);
										}}
									/>
								</Col>
							</Row>
						}
						{record.type === 'options' &&
							<DropdownView
								required
								allowNewTags
								label="Options"
								model={record.options}
								disabled={!isSelected || this.props.mode === 'display'}
								onChanged={(newValue) => {
									record.options = Array.isArray(newValue) ? newValue.map(m => m.value) : [newValue.value];
									this.props.onEditPropertyItem(record);
								}}
								request={this.getOptionsRequest(record.options)}
								keyField="value"
								textField="value"
							/>
						}
					</div>
				);
			}
		}
	];

	rowKey = (record: EntitySchemaProperty) => record.id;
	isValidItem = async (item: any) => this.props.isValidPropertyItem(item);

	render(): React.ReactNode {
		if (this.props.model == null) {
			return null;
		}

		return (
			<div className="window window-normal">
				<Row>
					<Col xs={24}>
						<Row>
							<Col xs={24}>
								<HeaderView
									title={`Schema Edition for ${this.props.schemaName}`}
									mode={this.props.mode}
									onModeChanged={this.props.onModeChange}
									save={this.props.onSave}
									cancel={this.props.onCancel}
									contextService={this.props.model}
								/>
							</Col>
						</Row>
						<Row>
							<Col xs={24}>
								<CardController
									title="Editable Properties"
									mode={this.props.mode}>
									<DataTableView
										model={this.props.model}
										columns={this.columns}
										rowKey={this.rowKey}
										mode={this.props.mode}
										setBusy={this.props.setBusy}
										isValidItem={this.isValidItem}
										createNewItem={this.props.createPropertyItem}
										afterDeleteItem={this.props.afterDeletePropertyItem}
									/>
								</CardController>
							</Col>
						</Row>
					</Col>
				</Row>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/entity-schemas/form/entity-schema-form.view.tsx